import { AskAIButtonProps } from "@/components/static-dashboard-components/charts-components/ChartTopBar";
import { useSelectedChart } from "@/contexts/ChartsContext";
import { Colors } from "@/enums/Colors";
import { MainDashBoardChart } from "@/enums/MainDashBoardChart";
import { AverageChartData } from "@/models/charts/AverageChartData";
import NorthIcon from "@mui/icons-material/North";
import SouthIcon from "@mui/icons-material/South";
import { Box, Switch, SwitchProps, Typography, styled } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useState } from "react";
import { CollapseChart } from "./CollapseChart";

interface Props {
  data: AverageChartData;
}

enum NetBurnCalcMethods {
  "LAST_MONTH_TOTAL" = "Last month total",
  "THREE_MONTH_AVERAGE" = "Three month average",
}

const US_DOLLAR = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
});

const NetBurn = ({ data, disableAskAIButton = false, disableExpand = false }: Props & AskAIButtonProps) => {
  const { selectedChart, setSelectedChart } = useSelectedChart();

  const { LAST_MONTH_TOTAL, THREE_MONTH_AVERAGE } = NetBurnCalcMethods;

  const [calcMethod, setCalcMethod] = useState<NetBurnCalcMethods>(NetBurnCalcMethods.LAST_MONTH_TOTAL);

  const renderPercentage = () => {
    const value = data?.currentPercentageValue;
    return (
      <>
        {value > 0 && "+"}
        <Typography fontWeight={400} sx={{ textAlign: "justify" }}>
          {parseFloat(value?.toFixed(2))}%
        </Typography>
        {value !== 0 && (value > 0 ? <NorthIcon style={{ color: "green" }} /> : <SouthIcon style={{ color: "red" }} />)}
      </>
    );
  };

  const value = calcMethod === LAST_MONTH_TOTAL ? data?.currentValue : data?.previousValue;
  const number = parseFloat(value?.toFixed(0)) || 0;

  const currencyColor = number == 0 ? Colors.BLACK_151517 : number > 0 ? Colors.GREEN_01A902 : Colors.RED;

  const getFormattedNumber = () => {
    let moneyAmount = `${number > 0 ? "+" : ""} ${US_DOLLAR.format(Math.trunc(number))}`;

    if (number < 0) moneyAmount = `(${moneyAmount})`;

    return moneyAmount;
  };

  const renderCollapseContent = () => {
    return (
      <Container>
        <Typography fontWeight={500} color={Colors.LIGHT_GRAY_8A8E93} fontSize={"12px"}>
          {netBurnDescription}
        </Typography>
        <Container className="date-control">
          <Typography>1 month</Typography>
          <FormControlLabel
            control={
              <DateSwitch
                sx={{ m: 1 }}
                onChange={() => {
                  setCalcMethod((prev) => (prev === LAST_MONTH_TOTAL ? THREE_MONTH_AVERAGE : LAST_MONTH_TOTAL));
                }}
              />
            }
            label=""
          />
          <Typography>3 months</Typography>
        </Container>
      </Container>
    );
  };

  return (
    <CollapseChart
      selected={selectedChart === MainDashBoardChart.NET_BURN}
      collapseContent={renderCollapseContent()}
      disableAskAIButton={disableAskAIButton}
      disableExpand={disableExpand}
      title={`Net Burn - ${calcMethod === THREE_MONTH_AVERAGE ? "last 3 Months Avg." : "Last Month"}`}
      value={getFormattedNumber()}
      colorValue={currencyColor}
      onClickStarIcon={() => {
        setSelectedChart(MainDashBoardChart.NET_BURN);
      }}
    />
  );
};

const Container = styled(Box)(() => ({
  "&.date-control": {
    paddingTop: "1rem",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    columnGap: "16px",
    "& > p": {
      fontWeight: 500,
      color: Colors.LIGHT_GRAY_8A8E93,
      fontSize: "12px",
    },
  },
  "& .MuiFormControlLabel-root": {
    margin: "0",
    "& .MuiSwitch-root": {
      margin: "0",
    },
  },
}));

const DateSwitch = styled((props: SwitchProps) => <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />)(
  ({ theme }) => ({
    width: 40,
    height: 24,
    padding: 0,
    "& .MuiSwitch-switchBase": {
      padding: 0,
      margin: 2,
      transitionDuration: "200ms",
      "&.Mui-checked": {
        transform: "translateX(16px)",
        color: "white",
        "& + .MuiSwitch-track": {
          backgroundColor: Colors.LAZO_PURPLE,
          opacity: 1,
          border: 0,
        },
        "&.Mui-disabled + .MuiSwitch-track": {
          opacity: 0.5,
        },
      },
      "&.Mui-focusVisible .MuiSwitch-thumb": {
        color: "#33cf4d",
        border: "6px solid #fff",
      },
      "&.Mui-disabled .MuiSwitch-thumb": {
        color: theme.palette.mode === "light" ? theme.palette.grey[100] : theme.palette.grey[600],
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
      },
    },
    "& .MuiSwitch-thumb": {
      boxSizing: "border-box",
      width: 20,
      height: 20,
    },
    "& .MuiSwitch-track": {
      borderRadius: 26 / 2,
      backgroundColor: Colors.LAZO_PURPLE,
      opacity: 1,
      transition: theme.transitions.create(["background-color"], {
        duration: 500,
      }),
    },
  })
);

const netBurnDescription =
  "Net Burn represents the total cash transactions imported from the integrated accounting software, and it excludes all non-operational cash movements like new investments or debt.";

export default NetBurn;
